import { atom } from 'jotai'
import invariant from 'tiny-invariant'
import { WatchtowerSteps } from './components/steps'

export type StepValidation = {
  isValid: boolean
  showValidationErrors: boolean
}

/**
 * This class gives us a map which will also type check that the
 * data stored against a given step is the right type. The shape
 * of the step data is defined by a type in the given step, and is
 * used to create the WatchtowerSteps type we import here.
 */
type StepsDataRecord = {
  [K in WatchtowerSteps as K['step']]: K['data']
}

type StepEntry<K extends StepKey> = {
  data: StepsDataRecord[K]
  /**
   * We allow for a given step to have multiple forms, each with its own validation state.
   */
  validation?: Record<string, StepValidation>
}

export const highlightedFieldPathsAtom = atom<string[] | undefined>()
/**
 * The steps map contains both the step data and the validation state
 * of the step. This gives us a lovely one stop shop for data management
 * for the step, and allows for easy - type safe - initialization of the
 * steps too.
 *
 * In this case of watchtower we use atoms in order to manage the usage
 * and access of this data from the steps anyway, but the getters/setters
 * of the map here still make it easy to evolve this datastructure!
 */
type StepKey = keyof StepsDataRecord
export class StepsMap extends Map<StepKey, StepEntry<StepKey>> {
  constructor(
    entries?:
      | {
          [K in StepKey]: [K, StepsDataRecord[K] | StepEntry<K>]
        }[StepKey][]
      | StepsMap
  ) {
    if (!entries) {
      super()
      return
    }

    /** If it's already a StepsMap, just copy it */
    if (entries instanceof StepsMap) {
      super(entries)
      return
    }

    /**
     * Transform entries to ensure they have the correct structure
     **/
    const transformedEntries = entries.map(([key, value]) => {
      /**
       * If the value provided is already a step entry, we can use it directly,
       * otherwise map the initial data onto the data property.
       */
      if (value && typeof value === 'object' && 'data' in value) {
        return [key, value as StepEntry<StepKey>]
      }
      return [key, { data: value, validation: {} } as StepEntry<StepKey>]
    }) as [StepKey, StepEntry<StepKey>][]

    super(transformedEntries)
  }

  get<K extends StepKey>(key: K): StepEntry<K> | undefined {
    return super.get(key) as StepEntry<K> | undefined
  }

  set<K extends StepKey>(key: K, value: StepEntry<K>) {
    return super.set(key, value)
  }

  getData<K extends StepKey>(key: K): StepsDataRecord[K] | undefined {
    const entry = this.get(key)
    return entry?.data
  }

  setData<K extends StepKey>(key: K, data: StepsDataRecord[K]) {
    const existingEntry = this.get(key)
    const newEntry: StepEntry<K> = {
      data,
      validation: existingEntry?.validation ?? {}
    }
    return this.set(key, newEntry)
  }

  getValidation<K extends StepKey>(key: K): Record<string, StepValidation> {
    const entry = this.get(key)
    return entry?.validation ?? {}
  }

  setValidation<K extends StepKey>(
    key: K,
    validation: Record<string, StepValidation>
  ) {
    const existingEntry = this.get(key)
    if (!existingEntry) return this

    const newEntry: StepEntry<K> = {
      data: existingEntry.data,
      validation
    }
    return this.set(key, newEntry)
  }
}

/**
 * Core data atoms - these should be included in the ScopeProvider
 */

export const currentStepIndexAtom = atom<number>(0)
export const stepsAtom = atom<StepsMap>(new StepsMap([]))
export const automationExecutionAtom = atom<string | undefined>(undefined)

export const currentStepAtom = atom<WatchtowerSteps['step']>(get => {
  const steps = get(stepsAtom)
  const currentIndex = get(currentStepIndexAtom)
  return [...steps.keys()][currentIndex]
})

export const goNextStepAtom = atom(null, (get, set) => {
  const steps = get(stepsAtom)
  const currentStepIndex = get(currentStepIndexAtom)

  if (currentStepIndex < steps.size - 1) {
    set(currentStepIndexAtom, currentStepIndex + 1)
  }
})

export const goPreviousStepAtom = atom(null, (get, set) => {
  const currentStepIndex = get(currentStepIndexAtom)
  if (currentStepIndex > 0) {
    set(currentStepIndexAtom, currentStepIndex - 1)
  }
  return
})

export const stepDataAtomFactory = <T extends WatchtowerSteps['step']>(
  step: T
) =>
  atom(
    get => {
      const taskData = get(stepsAtom)
      const stepData = taskData.getData(step)

      invariant(stepData, `Step data for ${step} not found`)

      return stepData
    },
    (get, set, update: Partial<StepsDataRecord[T]>) => {
      const currentStepsMap = get(stepsAtom)
      const existingStepData = currentStepsMap.getData(step)

      invariant(existingStepData, `No data found for step: ${step}`)

      const newStepsMap = new StepsMap(currentStepsMap)
      newStepsMap.setData(step, { ...existingStepData, ...update })
      set(stepsAtom, newStepsMap)
    }
  )

export const stepValidationAtomFactory = <T extends WatchtowerSteps['step']>(
  step: T,
  /**
   * Giving the form a name will make for easier human readability for when
   * there are multiple forms, but by default we generate a unique identifier
   * for scoping the validation state.
   *
   * tl;dr you don't have to include the formName - which is especially useful
   * when a given step only has one form to care about.
   */
  formName: string = crypto.randomUUID()
) =>
  atom(
    get => {
      const stepsMap = get(stepsAtom)
      const stepValidation = stepsMap.getValidation(step)

      return (
        stepValidation[formName] ?? {
          isValid: false,
          showValidationErrors: false
        }
      )
    },
    (get, set, update: Partial<StepValidation>) => {
      const currentStepsMap = get(stepsAtom)

      const currentStepValidation = currentStepsMap.getValidation(step)
      const currentFormValidation = currentStepValidation[formName] ?? {
        isValid: false,
        showValidationErrors: false
      }

      const newStepValidation = { ...currentFormValidation, ...update }
      const newValidation = {
        ...currentStepValidation,
        [formName]: newStepValidation
      }

      const newStepsMap = new StepsMap(currentStepsMap)
      newStepsMap.setValidation(step, newValidation)
      set(stepsAtom, newStepsMap)
    }
  )

export const isFinalStepAtom = atom(get => {
  const steps = get(stepsAtom)
  const currentStepIndex = get(currentStepIndexAtom)
  return currentStepIndex === steps.size - 1
})

type TaskDrawerConfig =
  | { type: 'PRICE'; phaseId: string; priceId: string | undefined }
  | { type: 'DISCOUNT'; phaseId: string; discountId: string | undefined }
  | { type: 'MINIMUM'; phaseId: string; minimumId: string | undefined }

export const taskDrawerConfigAtom = atom<TaskDrawerConfig | null>()
